import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { t } from "@/i18n-js/instance";
import {
  useCanvasHistoryNav,
  useCanvasInvokeBeforeGoBack,
  useCanvasPageTitleCrumb,
  useCanvasPageTitleParentCrumb,
} from "@/react/components/Layout/CircleAIAppShellLayout/CanvasHeader/CanvasHeaderContext";
import {
  CANVAS_MOBILE_HEADER_ACTIONS_ID,
  CANVAS_MOBILE_HEADER_LEFT_ACTIONS_ID,
  CANVAS_MOBILE_HEADER_TITLE_ACTIONS_ID,
} from "@/react/components/Layout/CircleAIAppShellLayout/CanvasHeader/constants";
import { primaryActionToneFix } from "@/react/components/Mobile/shell/topBar/mobileTopBarTokens";
import { UnthemedMobileTopNav } from "@/react/components/Mobile/shell/topBar/UnthemedMobileTopNav";

/**
 * Canvas/AI shell's title+back row, registered via topBarOverride. Uses
 * `UnthemedMobileTopNav` for the standard back-button/title/padding styling.
 */
export const CanvasMobileTopBar = () => {
  const pageTitleCrumb = useCanvasPageTitleCrumb();
  const pageTitleParentCrumb = useCanvasPageTitleParentCrumb();
  const invokeBeforeGoBack = useCanvasInvokeBeforeGoBack();
  const navigate = useNavigate();
  const { canGoBack, goBack } = useCanvasHistoryNav();

  const handleBack = useCallback(() => {
    if (canGoBack) {
      goBack();
      return;
    }

    if (invokeBeforeGoBack()) return;
    if (pageTitleParentCrumb?.href) {
      void navigate({ to: pageTitleParentCrumb.href, replace: true });
    }
  }, [
    canGoBack,
    goBack,
    invokeBeforeGoBack,
    navigate,
    pageTitleParentCrumb?.href,
  ]);

  return (
    <>
      <UnthemedMobileTopNav
        back={{
          onClick: handleBack,
          label: t("circle_ai_shell.canvas_header.go_back"),
        }}
        title={pageTitleCrumb?.label ?? ""}
        titleSize="md"
        primaryAction={
          <>
            <div
              id={CANVAS_MOBILE_HEADER_TITLE_ACTIONS_ID}
              className="flex shrink-0 items-center gap-2 empty:hidden"
            />
            <div
              id={CANVAS_MOBILE_HEADER_ACTIONS_ID}
              className="flex shrink-0 items-center gap-2 empty:hidden"
            />
          </>
        }
      />
      <div
        style={primaryActionToneFix}
        id={CANVAS_MOBILE_HEADER_LEFT_ACTIONS_ID}
        className="flex items-center gap-2 overflow-x-auto px-4 pb-2 empty:hidden"
      />
    </>
  );
};
